import { useState } from "react";
import NapFieldsGroup, { napFieldsComplete } from "./NapFieldsGroup";
import NapCodeMapLinks from "./NapCodeMapLinks";
import ButtonSpinner from "./ButtonSpinner";

/* ──────────────────────────────────────────────────────────────────────────
 * NAP / Completion Details card
 *
 * Groups the NAP completion fields (NAP Code, NAP ID, NAP Port, Vacant Ports,
 * Modem Serial Number, IPTV Serial Number, Completion Photo) into one
 * professional card shown in the NI Job Order and Migration Record detail
 * modals. A permission-gated Edit button lets authorized users correct these
 * details even after the record is Completed/Cancelled.
 *
 * Endpoint/field-name differences between the two modules are kept in the
 * parent: the parent passes the current values and an async `onSave(payload)`
 * handler that performs the photo upload + nap-details API calls and returns
 * the updated record (or throws with a message).
 * ──────────────────────────────────────────────────────────────────────────*/

function ReadCell({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
      {children}
    </div>
  );
}

function ReadValue({ value }) {
  return (
    <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border min-h-[36px]">
      {value === 0 ? "0" : (value || <span className="text-gray-400">—</span>)}
    </p>
  );
}

export default function NapCompletionCard({
  // current persisted values (normalized)
  napCode,
  napId,
  napPort,
  vacantPorts,
  modemSerial,
  iptvSerial,
  hasPhoto = false,
  // behavior
  canEdit = false,
  isTerminal = false,
  completed = false,
  onViewPhoto,
  renderNapCode,      // optional render-prop for the NAP Code view cell
  onSave,             // async ({ napCode, napId, napPort, vacantPorts, modemSerial, iptvSerial, reason, napPhotoFile }) => updatedRecord
  onSaved,            // (updatedRecord) => void — called after a successful save
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // edit-mode local state
  const [fCode, setFCode] = useState(napCode || "");
  const [fId, setFId] = useState(napId || "");
  const [fPort, setFPort] = useState(napPort || "");
  const [fVacant, setFVacant] = useState(vacantPorts ?? "");
  const [fModem, setFModem] = useState(modemSerial || "");
  const [fIptv, setFIptv] = useState(iptvSerial || "");
  const [fPhoto, setFPhoto] = useState(null);
  const [reason, setReason] = useState("");

  const startEdit = () => {
    setFCode(napCode || "");
    setFId(napId || "");
    setFPort(napPort || "");
    setFVacant(vacantPorts ?? "");
    setFModem(modemSerial || "");
    setFIptv(iptvSerial || "");
    setFPhoto(null);
    setReason("");
    setError("");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError("");
  };

  const canSave =
    napFieldsComplete({ napCode: fCode, napId: fId, napPort: fPort, vacantPorts: fVacant }) &&
    !!fModem.trim() &&
    (!isTerminal || !!reason.trim());

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError("");
    try {
      const updated = await onSave({
        napCode: fCode.trim(),
        napId: fId.trim(),
        napPort: fPort.trim(),
        vacantPorts: fVacant,
        modemSerial: fModem.trim(),
        iptvSerial: fIptv.trim(),
        reason: reason.trim(),
        napPhotoFile: fPhoto,
      });
      setEditing(false);
      onSaved?.(updated);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to save NAP details.");
    } finally {
      setSaving(false);
    }
  };

  const napCodeCell = renderNapCode
    ? renderNapCode()
    : (completed && napCode
        ? <NapCodeMapLinks napCode={napCode} />
        : <ReadValue value={napCode} />);

  return (
    <div className="border rounded-lg mt-3 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
          </svg>
          <h4 className="text-sm font-semibold text-gray-700">NAP / Completion Details</h4>
        </div>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={startEdit}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
        )}
      </div>

      {/* Body */}
      <div className="px-4 py-4">
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded px-3 py-2 mb-3">{error}</div>
        )}

        {!editing ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ReadCell label="NAP Code">{napCodeCell}</ReadCell>
            <ReadCell label="NAP ID"><ReadValue value={napId} /></ReadCell>
            <ReadCell label="NAP Port"><ReadValue value={napPort} /></ReadCell>
            <ReadCell label="Number of Vacant Ports"><ReadValue value={vacantPorts} /></ReadCell>
            <ReadCell label="Modem Serial Number"><ReadValue value={modemSerial} /></ReadCell>
            <ReadCell label="IPTV Serial Number"><ReadValue value={iptvSerial} /></ReadCell>
            <ReadCell label="Completion Photo">
              {hasPhoto ? (
                <button
                  type="button"
                  onClick={onViewPhoto}
                  title="View the NAP/completion photo"
                  className="w-full inline-flex items-center justify-center gap-1.5 border rounded px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100"
                >
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4-4 4 4 4-6 4 6M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                  </svg>
                  View Photo
                </button>
              ) : (
                <ReadValue value={null} />
              )}
            </ReadCell>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <div className="md:col-span-2">
              <NapFieldsGroup
                napCode={fCode} onNapCodeChange={setFCode}
                napId={fId} onNapIdChange={setFId}
                napPort={fPort} onNapPortChange={setFPort}
                vacantPorts={fVacant} onVacantPortsChange={setFVacant}
                showPhoto napPhoto={fPhoto} onNapPhotoChange={setFPhoto}
                labelClassName="block text-xs font-medium text-gray-500 mb-0.5"
              />
              {hasPhoto && !fPhoto && (
                <p className="text-xs text-gray-400 -mt-1 mb-3">
                  A completion photo is already on file. Choosing a new image replaces it.
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">
                Modem Serial Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={fModem}
                onChange={(e) => setFModem(e.target.value)}
                placeholder="Enter Modem Serial Number..."
                className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">IPTV Serial Number</label>
              <input
                type="text"
                value={fIptv}
                onChange={(e) => setFIptv(e.target.value)}
                placeholder="Enter IPTV Serial Number..."
                className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {isTerminal && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-500 mb-0.5">
                  Reason for correction <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="Explain why these details are being corrected (logged to NAP history)…"
                  className="w-full border rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            <div className="md:col-span-2 flex items-center justify-end gap-2 mt-1">
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className="px-3 py-1.5 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave || saving}
                className="px-4 py-1.5 text-sm text-white rounded disabled:opacity-50 bg-blue-600 hover:bg-blue-700 inline-flex items-center gap-2"
              >
                {saving && <ButtonSpinner />}
                {saving ? "Saving…" : "Save NAP Details"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
